import type { UpdateOwnerInput } from '@chukua/common';
import { Alert, Button, Form, Input } from 'antd';
import { useEffect } from 'react';
import type { OwnerView } from './owners.api';
import { PayoutMethodFields } from './PayoutMethodFields';

interface OwnerDetailsFormProps {
  owner: OwnerView;
  canManage: boolean;
  error: string | null;
  saving: boolean;
  onSave: (input: UpdateOwnerInput) => Promise<boolean>;
}

function toFormValues(owner: OwnerView): UpdateOwnerInput {
  return { name: owner.name, phone: owner.phone ?? '', email: owner.email ?? '', kraPin: owner.kraPin ?? '', payoutDetails: owner.payoutDetails };
}

/** The owner's fields, editable inline for `owner.manage` holders — read-only for everyone else with `owner.view` (CLAUDE.md: nav-hiding and disabled controls are UX only, the server remains the authority). */
export function OwnerDetailsForm({ owner, canManage, error, saving, onSave }: OwnerDetailsFormProps) {
  const [form] = Form.useForm<UpdateOwnerInput>();

  useEffect(() => {
    form.setFieldsValue(toFormValues(owner));
  }, [owner, form]);

  async function submit(values: UpdateOwnerInput) {
    await onSave(values);
  }

  return (
    <Form<UpdateOwnerInput>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={!canManage || saving}
      initialValues={toFormValues(owner)}
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
        <Input />
      </Form.Item>
      <div className="grid grid-cols-2 gap-3">
        <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Enter a phone number' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
          <Input />
        </Form.Item>
      </div>
      <Form.Item name="kraPin" label="KRA PIN" rules={[{ required: true, pattern: /^[A-Za-z]\d{9}[A-Za-z]$/, message: 'One letter, nine digits, one letter' }]}>
        <Input className="uppercase" />
      </Form.Item>
      <PayoutMethodFields />
      {canManage && (
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save changes
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}
